import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { startCheckout } from "@/lib/api";

// Mirrors backend PACKS: $1 per decode, 100 pack gets 20 bonus decodes
export const PACKS = Array.from({ length: 20 }, (_, i) => {
  const price = (i + 1) * 5;
  return { key: `pack_${price}`, price, decodes: price === 100 ? 120 : price };
});

export const PricingDialog = ({ open, onOpenChange, outOfDecodes = false }) => {
  const [selected, setSelected] = useState("pack_10");
  const [busy, setBusy] = useState(false);
  const pack = PACKS.find((p) => p.key === selected);

  const buy = async () => {
    setBusy(true);
    try {
      const { checkout_url } = await startCheckout(selected);
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#09090B] border-white/10 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pricing-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono-display text-2xl tracking-tight">Buy a decode pack</DialogTitle>
          <DialogDescription className="text-zinc-400">
            {outOfDecodes ? "You're out of decodes. " : ""}$1 per decode, no subscription. Decodes never expire.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mt-2" role="radiogroup" aria-label="Decode packs">
          {PACKS.map((p) => {
            const on = p.key === selected;
            const bonus = p.decodes > p.price;
            return (
              <button
                key={p.key}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setSelected(p.key)}
                data-testid={`pack-${p.price}`}
                className={`relative border rounded-sm py-3 px-2 text-center transition-colors ${
                  on ? "border-emerald-500 bg-emerald-500/10" : bonus ? "border-fuchsia-400/50 hover:border-fuchsia-400" : "border-white/10 hover:border-emerald-500/50"
                } ${bonus ? "col-span-2 sm:col-span-1" : ""}`}
              >
                {bonus && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-fuchsia-500 text-black font-mono-display text-[9px] font-bold tracking-widest uppercase px-1.5 whitespace-nowrap">
                    +20 free
                  </span>
                )}
                <div className={`font-mono-display text-lg font-black ${on ? "text-emerald-500" : "text-white"}`}>{p.decodes}</div>
                <div className="font-mono-display text-[10px] tracking-[0.15em] uppercase text-zinc-500">${p.price}</div>
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-4">
          <div data-testid="selected-pack-summary">
            <div className="font-mono-display text-3xl font-black">${pack.price}</div>
            <div className="text-sm text-zinc-400 flex items-center gap-1.5">
              <Check className="w-4 h-4 text-emerald-500" />
              {pack.decodes} decodes{pack.decodes > pack.price ? ` (${pack.price} + 20 bonus)` : ""}, each with prompt + lyrics
            </div>
          </div>
          <Button
            onClick={buy}
            disabled={busy}
            data-testid="buy-pack-btn"
            className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono-display text-xs tracking-[0.2em] uppercase font-bold h-12 px-8"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Buy ${pack.decodes} decodes`}
          </Button>
        </div>
        <p className="text-[11px] text-zinc-500 font-mono-display">Secure one-time checkout by Stripe.</p>
      </DialogContent>
    </Dialog>
  );
};
